import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, Check, Clock, Lock, AlertTriangle } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useT, useLang } from '@/i18n';
import { useAdminAgentProgress } from '@/features/agent-progress/progressHooks';
import {
  STREAK_DIAMANTE_DE_CONSTANCIA_MIN,
  type AdminProgressSummary,
  type TierName,
  type TierThresholdsShape,
} from '@/features/agent-progress/tierConstants';
import { ErrorState, Skeleton } from '@/components/chassis/States';
import { AgentDetailModal } from './AgentDetailModal';

/**
 * F5-R8c · «Progreso del agente» — la ficha que abre una fila de la tabla
 * Admin 03 (dibujo del CEO, 2026-08-29). Panel de ancho completo que
 * REEMPLAZA al ProgressModal admin (dos gráficos vacíos, cinco medallas
 * grises, todo en inglés).
 *
 * Contratos del dibujo que este archivo cumple:
 * - UNA serie con selector Semana · Mes · Año (12 semanas / 12 meses / 3
 *   años, del endpoint admin extendido) — barras siempre dibujadas, cero =
 *   3px gris, valor encima y tick abajo; línea + nota de meta en semana y
 *   mes, en la anual no («un año no tiene meta»).
 * - La escalera son cuatro peldaños-COLUMNA (56·74·92·110px): bloque,
 *   nombre y número son hijos del mismo contenedor, así la etiqueta no
 *   PUEDE desalinearse («ahí muere el problema de alineación»). Motion del
 *   dibujo: riseIn 520ms escalonado 90ms, relleno 900ms con overshoot,
 *   sweep 3.4s + halo 2.6s SOLO en el peldaño actual, «Acá está» con bob.
 * - Umbrales y meta: SIEMPRE del server (thresholds anclados + targetToday
 *   de profile-summary) — las dos firmas del dibujo (TIER_THRESHOLDS y
 *   STREAK_DAILY_TARGET hardcodeados) murieron en tierConstants.ts.
 * - Los 4 logros de nivel se DERIVAN de esos umbrales (jamás escritos a
 *   mano) y el contador «N de M» sale de contarlos.
 * - CERO montos: la tarjeta de estimación de bono se removió y no vuelve.
 *
 * Decisiones documentadas (suben al reporte):
 * - Chip de nivel = tierDisplayed del server (el nivel que el sistema le
 *   reconoce, mantenido incluido); la escalera marca tierAchievedThisMonth
 *   («SU TRAMO del mes» — el título de la tarjeta lo dice). Pueden diferir
 *   legítimamente si mantiene un nivel del mes pasado.
 * - Meta de la vista semanal = targetToday × 5 (precedente «ritmo de la
 *   semana» de esta misma pantalla); meta mensual = umbral del tramo
 *   actual (Bronce si todavía no llegó) — el «tramo» del goalNote.
 * - Sublínea del header: solo el rol. Turno y fecha de ingreso no viajan
 *   en ningún endpoint admin (NECESITA-SERVER).
 * - «Exportar su ficha»: sin endpoint — deshabilitado con diagnóstico.
 */

/** Rampa teal del precedente (tabla Admin 03 / dibujo) — literales declarados. */
const RAMP: Record<Exclude<TierName, 'below_floor'>, string> = {
  base: '#cfe4e5',
  t1: '#8fc3c4',
  t2: '#4f9fa1',
  t3: '#228a8d',
};
const SWATCH_BELOW = '#dde3e7';

const TIER_ORDER = ['base', 't1', 't2', 't3'] as const;
const STEP_HEIGHTS = [56, 74, 92, 110] as const;

const TIER_LABEL_KEY = {
  below_floor: 'tier.caminoBronce',
  base: 'tier.bronce',
  t1: 'tier.plata',
  t2: 'tier.oro',
  t3: 'tier.diamante',
} as const;

/** Grises del dibujo para las barras (v===0 · <35% del máximo) — literales. */
const BAR_ZERO = '#dde3e7';
const BAR_LOW = '#9aa7b2';
const BAR_LAST = '#122b44';
const BAR_TEAL = '#228a8d';

/** Keyframes del dibujo, con namespace r8c para no chocar con otros drafts.
 *  El media-query apaga TODO el motion del panel (los estilos inline no
 *  pueden usar motion-reduce:). */
const R8C_CSS = `
@keyframes r8c-rise { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
@keyframes r8c-sweep { 0% { transform:translateX(-170%) skewX(-16deg); } 60%,100% { transform:translateX(260%) skewX(-16deg); } }
@keyframes r8c-bob { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-4px); } }
@keyframes r8c-halo { 0%,100% { opacity:.30; transform:scale(1); } 50% { opacity:.08; transform:scale(1.10); } }
@media (prefers-reduced-motion: reduce) {
  [data-r8c] *, [data-r8c] { animation: none !important; transition: none !important; }
}
`;

type Gran = 'semana' | 'mes' | 'anio';

interface Pt {
  key: string;
  value: number;
  tick: string;
}

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('');
}

/** Número de semana ISO-8601 a partir de un YYYY-MM-DD (lunes NY). */
function isoWeek(nyDate: string): number {
  const d = new Date(nyDate + 'T12:00:00Z');
  const day = (d.getUTCDay() + 6) % 7;
  const thursday = new Date(d);
  thursday.setUTCDate(d.getUTCDate() - day + 3);
  const firstThursday = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 4));
  const fDay = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - fDay + 3);
  return 1 + Math.round((thursday.getTime() - firstThursday.getTime()) / (7 * 86400000));
}

const RING_R = 45;
const RING_CIRC = 2 * Math.PI * RING_R;

export interface AgentProgressPanelProps {
  agentId: number;
  onBack: () => void;
  /** Escalera viva de system_config (la que la página ya carga) — fallback
   *  si el server todavía no manda `thresholds` anclados. */
  fallbackLadder?: TierThresholdsShape | null;
}

export function AgentProgressPanel({ agentId, onBack, fallbackLadder }: AgentProgressPanelProps): JSX.Element {
  const t = useT();
  const lang = useLang();
  const q = useAdminAgentProgress(agentId);
  const [gran, setGran] = useState<Gran>('mes');
  const [showScreenings, setShowScreenings] = useState(false);

  // Motion de montaje: anillo y rellenos arrancan en 0 y transicionan a su
  // valor real (900ms overshoot en la escalera, como el dibujo).
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setArmed(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const fmt = (n: number): string => n.toLocaleString(lang === 'es' ? 'es-AR' : 'en-US');
  const monthLong = (m: number, y?: number): string =>
    new Intl.DateTimeFormat(lang === 'es' ? 'es' : 'en', { month: 'long' }).format(
      new Date(Date.UTC(y ?? 2026, m - 1, 15)),
    );
  const monthShort = (m: number): string =>
    new Intl.DateTimeFormat(lang === 'es' ? 'es' : 'en', { month: 'short' }).format(
      new Date(Date.UTC(2026, m - 1, 15)),
    );

  const s: AdminProgressSummary | undefined = q.data;

  // ---- Derivaciones (todas del server; el panel no re-declara números) ----
  const th: TierThresholdsShape | null = s?.thresholds ?? fallbackLadder ?? null;
  const monthTotal = s?.monthly.screeningsMonth ?? 0;
  const target = s?.today.targetToday ?? 0;
  const todayCount = s?.today.screeningsToday ?? 0;
  const daysWorking = s?.streak.daysWorking ?? 0;
  const activeDays = s?.activeDaysMonth ?? null;
  const periodMonthName = s ? monthLong(s.monthly.periodMonth, s.monthly.periodYear) : '';

  // Escalera: el marcador es el tramo ALCANZADO este mes (el server ya lo
  // clasificó con los umbrales anclados) — la tarjeta se llama «Su tramo».
  const achievedIdx = s ? TIER_ORDER.indexOf(s.monthly.tierAchievedThisMonth as (typeof TIER_ORDER)[number]) : -1;
  const targetIdx = achievedIdx + 1 <= 3 ? achievedIdx + 1 : -1;

  // Mejor mes de la historia (cerrados) y mejor "de siempre" (mes vivo incl.).
  const bestClosed = s?.achievements.bestMonth?.totalScreenings ?? 0;
  const bestEver = Math.max(bestClosed, monthTotal);

  // Estado propio: agente sin screenings («Todavía no envió ninguno este mes»).
  const virgin = s != null && monthTotal === 0 && todayCount === 0 && bestEver === 0 && daysWorking === 0;

  // Estado propio: racha cortada hoy — el día laborable más reciente que no
  // llegó a la meta, sacado de last7Days.
  const cutDay = useMemo(() => {
    if (!s || daysWorking > 0) return null;
    const days = [...s.last7Days].reverse();
    for (const d of days) {
      if (!d.isWorkingDay) continue;
      if (!d.hitTarget) return d.nyDate;
      break;
    }
    return null;
  }, [s, daysWorking]);

  // ---- La serie ----
  const hasSeries = s?.series != null;
  const buckets: Pt[] = useMemo(() => {
    if (!s) return [];
    if (gran === 'semana') {
      return (s.series?.weekly12 ?? []).map((w) => ({
        key: w.weekStartNy,
        value: w.screenings,
        tick: (lang === 'es' ? 'S' : 'W') + isoWeek(w.weekStartNy),
      }));
    }
    if (gran === 'anio') {
      return (s.series?.yearly3 ?? []).map((y) => ({
        key: String(y.periodYear),
        value: y.screenings,
        tick: String(y.periodYear),
      }));
    }
    // mes — con fallback honesto desde history12 si el server es viejo.
    const src =
      s.series?.monthly12 ??
      (() => {
        const closed = [...s.history12].reverse().filter(
          (h) => !(h.periodYear === s.monthly.periodYear && h.periodMonth === s.monthly.periodMonth),
        );
        return [...closed, { periodYear: s.monthly.periodYear, periodMonth: s.monthly.periodMonth, totalScreenings: monthTotal }]
          .slice(-12)
          .map((h) => ({ periodYear: h.periodYear, periodMonth: h.periodMonth, screenings: h.totalScreenings }));
      })();
    return src.map((m) => ({
      key: `${m.periodYear}-${m.periodMonth}`,
      value: m.screenings,
      tick: monthShort(m.periodMonth),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s, gran, lang]);

  const vals = buckets.map((b) => b.value);
  const sMax = vals.length > 0 ? Math.max(...vals) : 0;
  const sTotal = vals.reduce((a, b) => a + b, 0);
  const activeBuckets = vals.filter((v) => v > 0).length;

  // Meta de la vista: semana = targetToday×5 (precedente «ritmo de la
  // semana»); mes = umbral del tramo actual (o Bronce si aún no llegó);
  // año = sin meta («un año no tiene meta»).
  const goal: number | null =
    gran === 'semana'
      ? target > 0 ? target * 5 : null
      : gran === 'mes'
        ? th ? (achievedIdx >= 0 ? th[TIER_ORDER[achievedIdx]!] : th.base) : null
        : null;
  const goalHit = goal != null ? vals.filter((v) => v >= goal).length : 0;

  const granDef: Record<Gran, { label: string; unit: string; unitActive: string; plural: string }> = {
    semana: {
      label: t('f5r8c.vol.gran.semana'),
      unit: t('f5r8c.vol.unit.semana'),
      unitActive: t('f5r8c.vol.unitActive.semana'),
      plural: t('f5r8c.vol.plural.semana'),
    },
    mes: {
      label: t('f5r8c.vol.gran.mes'),
      unit: t('f5r8c.vol.unit.mes'),
      unitActive: t('f5r8c.vol.unitActive.mes'),
      plural: t('f5r8c.vol.plural.mes'),
    },
    anio: {
      label: t('f5r8c.vol.gran.anio'),
      unit: t('f5r8c.vol.unit.anio'),
      unitActive: t('f5r8c.vol.unitActive.anio'),
      plural: t('f5r8c.vol.plural.anio'),
    },
  };

  // Estado propio: primer día (una sola barra con datos, y el rótulo lo dice).
  const firstDay = s != null && todayCount > 0 && monthTotal === todayCount && bestClosed === 0 && (activeDays == null || activeDays <= 1);

  const granNote = ((): string => {
    const base =
      gran === 'semana'
        ? t('f5r8c.vol.note.semana')
        : gran === 'mes'
          ? t('f5r8c.vol.note.mes')
          : t('f5r8c.vol.note.anio', {
              y0: buckets[0]?.tick ?? '',
              y1: buckets[buckets.length - 1]?.tick ?? '',
            });
    return firstDay ? `${base} · ${t('f5r8c.vol.firstDay')}` : base;
  })();

  // ---- Logros: los 4 de nivel DERIVADOS de los umbrales del server ----
  interface Ach {
    key: string;
    name: string;
    note: string;
    earned: boolean;
  }
  const achievements: Ach[] = useMemo(() => {
    if (!s || !th) return [];
    const historyAsc = [...s.history12].reverse();
    const reachedLabel = (min: number): string => {
      const hit = historyAsc.find((h) => h.totalScreenings >= min);
      if (hit) return monthLong(hit.periodMonth, hit.periodYear);
      if (monthTotal >= min) return periodMonthName;
      const bm = s.achievements.bestMonth;
      if (bm && bm.totalScreenings >= min) return monthLong(bm.periodMonth, bm.periodYear);
      return '—';
    };
    const tierAch: Ach[] = TIER_ORDER.map((tier) => {
      const min = th[tier];
      const earned = bestEver >= min;
      return {
        key: tier,
        name: t(TIER_LABEL_KEY[tier]),
        earned,
        note: earned
          ? t('f5r8c.ach.reached', { month: reachedLabel(min), th: fmt(min) })
          : t('f5r8c.ach.locked', { n: fmt(Math.max(0, min - monthTotal)), th: fmt(min) }),
      };
    });
    const constancia: Ach = {
      key: 'constancia',
      name: t('f5r8c.ach.constanciaName'),
      earned: s.achievements.diamanteDeConstancia,
      note: t('f5r8c.ach.constanciaNote', { min: STREAK_DIAMANTE_DE_CONSTANCIA_MIN, d: daysWorking }),
    };
    const bestPeriod =
      monthTotal >= bestClosed && monthTotal > 0
        ? { m: s.monthly.periodMonth, y: s.monthly.periodYear }
        : s.achievements.bestMonth
          ? { m: s.achievements.bestMonth.periodMonth, y: s.achievements.bestMonth.periodYear }
          : null;
    const best: Ach = {
      key: 'best',
      name: t('f5r8c.ach.bestName'),
      earned: bestEver > 0,
      note:
        bestEver > 0 && bestPeriod
          ? t('f5r8c.ach.bestNote', { n: fmt(bestEver), month: monthLong(bestPeriod.m, bestPeriod.y), year: bestPeriod.y })
          : t('f5r8c.ach.bestLocked'),
    };
    return [...tierAch, constancia, best];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s, th, bestEver, monthTotal, daysWorking, lang]);
  const achEarned = achievements.filter((a) => a.earned).length;

  // ---- Render ----
  const kicker = 'm-0 mb-4 font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-teal';
  const card = 'bg-white border-[1.5px] border-nav-card rounded-xl px-[22px] py-5';
  const ghostBtn =
    'inline-flex items-center min-h-[44px] px-4 bg-white text-nav-navy border-[1.5px] border-nav-line rounded-lg font-head text-[12.5px] font-bold transition-colors duration-150 motion-reduce:transition-none hover:bg-nav-tealTint hover:border-nav-teal hover:text-nav-tealDark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2';

  const tierDisplayed = (s?.monthly.tierDisplayed ?? 'below_floor') as TierName;
  const chipSwatch = tierDisplayed === 'below_floor' ? SWATCH_BELOW : RAMP[tierDisplayed];

  const ringPct = target > 0 ? Math.min(1, todayCount / target) : 0;
  const ringDash = armed ? Math.round(RING_CIRC * ringPct) : 0;

  return (
    <div data-r8c className="bg-nav-soft border-[1.5px] border-nav-card rounded-[14px] px-7 pt-[26px] pb-8 animate-fade-up-slow motion-reduce:animate-none">
      <style>{R8C_CSS}</style>

      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-2 min-h-[44px] pl-2.5 pr-3.5 rounded-lg font-head text-[13px] font-bold text-nav-teal transition-colors duration-150 motion-reduce:transition-none hover:bg-nav-tealTint hover:text-nav-tealDark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2"
      >
        <ChevronLeft className="w-4 h-4 flex-none" strokeWidth={2} aria-hidden />
        {t('f5r8c.back')}
      </button>

      {q.isLoading ? (
        <div className="mt-4 space-y-4" aria-busy="true">
          <Skeleton className="h-[72px] rounded-xl" />
          <div className="grid [grid-template-columns:repeat(auto-fit,minmax(320px,1fr))] gap-4">
            <Skeleton className="h-[260px] rounded-xl" />
            <Skeleton className="h-[260px] rounded-xl" />
          </div>
          <Skeleton className="h-[280px] rounded-xl" />
        </div>
      ) : q.error || !s ? (
        <ErrorState
          icon={<AlertTriangle className="w-6 h-6" aria-hidden />}
          title={t('f5r8c.err.title')}
          body={t('f5r8c.err.body')}
          action={{ label: t('f5r8c.err.retry'), onClick: () => { void q.refetch(); } }}
        />
      ) : (
        <>
          {/* ---- Header: avatar · nombre · chip de nivel · acciones ---- */}
          <div className="flex items-start justify-between gap-5 flex-wrap mt-[18px] mb-[22px]">
            <div className="flex items-center gap-4 min-w-0">
              <span
                aria-hidden
                className="flex-none inline-flex items-center justify-center w-14 h-14 rounded-full bg-nav-navy text-white font-head text-[18px] font-bold"
              >
                {initials(s.displayName)}
              </span>
              <div className="min-w-0">
                <div className="flex items-center gap-2.5 flex-wrap mb-[5px]">
                  <h1 className="m-0 font-head text-2xl font-extrabold tracking-[-0.02em] text-nav-navy">
                    {s.displayName}
                  </h1>
                  <span className="inline-flex items-center gap-2 bg-nav-tealTint text-nav-tealDark border border-[#c9e2e2] rounded-full px-3 py-[5px] font-head text-[11.5px] font-bold">
                    <span aria-hidden className="w-[11px] h-[11px] rounded-[3px] flex-none" style={{ backgroundColor: chipSwatch }} />
                    {t(TIER_LABEL_KEY[tierDisplayed])}
                  </span>
                </div>
                {/* NECESITA-SERVER: turno y fecha de ingreso no viajan en
                    ningún endpoint admin — la subline queda en el rol. */}
                <p className="m-0 text-[13px] text-nav-slate">{t('f5r8c.role')}</p>
              </div>
            </div>
            <div className="flex gap-2.5 flex-wrap">
              <button type="button" className={ghostBtn} onClick={() => setShowScreenings(true)}>
                {t('f5r8c.seeScreenings')}
              </button>
              {/* Sin endpoint de export individual — deshabilitado con diagnóstico. */}
              <button
                type="button"
                disabled
                title={t('f5r8c.exportDiag')}
                aria-describedby="r8c-export-diag"
                className={cn(ghostBtn, 'opacity-40 cursor-not-allowed hover:bg-white hover:border-nav-line hover:text-nav-navy')}
              >
                {t('f5r8c.export')}
              </button>
              <span id="r8c-export-diag" className="sr-only">{t('f5r8c.exportDiag')}</span>
            </div>
          </div>

          {/* ---- Estado propio: sin screenings (salvia, nunca error) ---- */}
          {virgin && (
            <div className="flex items-center gap-2.5 bg-nav-sageTint border-[1.5px] border-nav-sage rounded-xl px-[18px] py-3.5 mb-4 text-[13.5px] font-semibold text-nav-sageText">
              {t('f5r8c.virgin')}
            </div>
          )}

          <div className="grid [grid-template-columns:repeat(auto-fit,minmax(320px,1fr))] gap-4 mb-4">
            {/* ==== Tarjeta HOY ==== */}
            <div className={card}>
              <p className={kicker}>{t('f5r8c.hoy.kicker')}</p>
              <div className="flex items-center gap-5 flex-wrap">
                <span className="relative flex-none inline-flex items-center justify-center w-[104px] h-[104px]">
                  <svg width="104" height="104" viewBox="0 0 104 104" aria-hidden className="block">
                    <circle cx="52" cy="52" r={RING_R} fill="none" stroke="#eef1f3" strokeWidth="9" />
                    <circle
                      cx="52"
                      cy="52"
                      r={RING_R}
                      fill="none"
                      stroke={BAR_TEAL}
                      strokeWidth="9"
                      strokeLinecap="round"
                      strokeDasharray={`${ringDash} ${Math.round(RING_CIRC)}`}
                      transform="rotate(-90 52 52)"
                      style={{ transition: 'stroke-dasharray .9s cubic-bezier(.34,1.56,.64,1)' }}
                    />
                  </svg>
                  {/* El anillo lleva su cifra en TEXTO, no sólo en el arco (a11y). */}
                  <span className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="font-head text-[26px] font-extrabold leading-none tracking-[-0.02em] text-nav-navy tabular-nums">
                      {todayCount}
                    </span>
                    <span className="mt-[3px] font-body text-[11.5px] font-semibold text-nav-slate tabular-nums">
                      {t('f5r8c.hoy.of', { n: fmt(target) })}
                    </span>
                  </span>
                </span>
                <div className="flex-1 min-w-[150px]">
                  <p className="m-0 mb-2 text-[13.5px] leading-relaxed">
                    {todayCount >= target && target > 0
                      ? t('f5r8c.hoy.lineDone', { c: fmt(todayCount), m: fmt(target) })
                      : t('f5r8c.hoy.line', { c: fmt(todayCount), m: fmt(target), r: fmt(Math.max(0, target - todayCount)) })}
                  </p>
                  <p className="m-0 text-[12.5px] leading-[1.55] text-nav-slate">
                    {t('f5r8c.hoy.noteA')}
                    <b>{t('f5r8c.hoy.noteCfg')}</b>
                    {t('f5r8c.hoy.noteB')}
                    <b>agent_daily_target</b>
                    {t('f5r8c.hoy.noteC')}
                  </p>
                </div>
              </div>

              {/* El pendiente de validación, explicado — regla de plata. */}
              {s.pendingValidation.monthCount > 0 && (
                <div className="flex items-start gap-2.5 mt-[18px] bg-nav-amberTint border-[1.5px] border-nav-amber rounded-[10px] px-[15px] py-[13px] font-body text-[12.5px] leading-[1.55] text-nav-amberText">
                  <Clock className="w-4 h-4 flex-none mt-[1px]" strokeWidth={2} aria-hidden />
                  <span className="flex-1 min-w-[180px]">
                    <b>
                      {s.pendingValidation.monthCount === 1
                        ? t('f5r8c.pend.leadOne')
                        : t('f5r8c.pend.lead', { n: fmt(s.pendingValidation.monthCount) })}
                    </b>{' '}
                    {t('f5r8c.pend.body', { today: fmt(todayCount) })}
                  </span>
                </div>
              )}
            </div>

            {/* ==== Tarjeta SU TRAMO EN LA ESCALERA ==== */}
            <div className={card}>
              <p className={kicker}>{t('f5r8c.esc.kicker')}</p>

              <div className="flex items-baseline gap-2 mb-3.5">
                <span className="font-head text-[30px] font-extrabold leading-none tracking-[-0.02em] tabular-nums">
                  {fmt(monthTotal)}
                </span>
                <span className="font-body text-[13px] text-nav-slate">
                  {t('f5r8c.esc.of', { month: periodMonthName })}
                </span>
              </div>

              {th ? (
                <>
                  <div className="flex items-end gap-[9px]">
                    {TIER_ORDER.map((tier, i) => {
                      const min = th[tier];
                      const reached = i <= achievedIdx;
                      const isTarget = i === targetIdx;
                      const here = i === achievedIdx;
                      const prevMin = i === 0 ? 0 : th[TIER_ORDER[i - 1]!];
                      const fillPct = reached
                        ? 100
                        : isTarget
                          ? Math.max(4, Math.round(((monthTotal - prevMin) / Math.max(1, min - prevMin)) * 100))
                          : 0;
                      const color = RAMP[tier];
                      const aria = reached
                        ? t('f5r8c.esc.ariaReached', { name: t(TIER_LABEL_KEY[tier]), th: fmt(min) })
                        : t('f5r8c.esc.ariaMissing', { name: t(TIER_LABEL_KEY[tier]), th: fmt(min), n: fmt(Math.max(0, min - monthTotal)) });
                      // Estructura del dibujo: bloque, nombre y número son
                      // hijos del MISMO contenedor — la etiqueta no puede
                      // desalinearse de su peldaño.
                      return (
                        <div
                          key={tier}
                          role="img"
                          aria-label={aria}
                          className="flex-1 min-w-0 flex flex-col items-stretch"
                          style={{ animation: 'r8c-rise .52s cubic-bezier(.34,1.2,.64,1) both', animationDelay: `${i * 0.09}s` }}
                        >
                          <span className="block h-6 text-center">
                            {here && (
                              <span
                                className="inline-block bg-nav-navy text-white rounded-full px-2.5 py-[3px] font-head text-[9.5px] font-extrabold tracking-[.04em] whitespace-nowrap"
                                style={{ animation: 'r8c-bob 2.8s ease-in-out infinite' }}
                              >
                                {t('f5r8c.esc.here')}
                              </span>
                            )}
                          </span>

                          <span
                            className="relative flex items-end justify-center rounded-[9px] bg-nav-hair overflow-hidden border-[1.5px]"
                            style={{ height: `${STEP_HEIGHTS[i]}px`, borderColor: here ? color : '#e3e8eb' }}
                          >
                            {here && (
                              <span
                                aria-hidden
                                className="absolute -inset-[7px] rounded-[13px] pointer-events-none"
                                style={{ background: color, opacity: 0.2, animation: 'r8c-halo 2.6s ease-in-out infinite' }}
                              />
                            )}
                            <span
                              aria-hidden
                              className="absolute left-0 right-0 bottom-0 overflow-hidden"
                              style={{
                                height: `${armed ? fillPct : 0}%`,
                                background: color,
                                transition: 'height .9s cubic-bezier(.34,1.56,.64,1)',
                              }}
                            >
                              {here && (
                                <span
                                  aria-hidden
                                  className="absolute top-0 bottom-0 w-[38%]"
                                  style={{
                                    background: 'linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,.55), rgba(255,255,255,0))',
                                    animation: 'r8c-sweep 3.4s ease-in-out infinite',
                                  }}
                                />
                              )}
                            </span>
                            <span
                              className="relative pb-[7px] font-head text-[11px] font-extrabold tabular-nums"
                              style={{ color: fillPct > 24 ? (i >= 2 ? '#fff' : '#1a6a6d') : '#6b7c8c' }}
                            >
                              {reached ? '100%' : isTarget ? `${fillPct}%` : ''}
                            </span>
                          </span>

                          <span
                            className={cn('block mt-[9px] text-center font-head text-[11.5px]', here ? 'font-extrabold' : 'font-bold')}
                            style={{ color: reached || isTarget ? '#122b44' : '#6b7c8c' }}
                          >
                            {t(TIER_LABEL_KEY[tier])}
                          </span>
                          <span className="block mt-[3px] text-center font-body text-[10.5px] font-medium text-nav-slate tabular-nums">
                            {fmt(min)}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  <p aria-live="polite" className="m-0 mt-[18px] pt-3.5 border-t border-nav-hair text-[13px] leading-relaxed">
                    {targetIdx >= 0
                      ? t('f5r8c.esc.note', {
                          cur: t(TIER_LABEL_KEY[(achievedIdx >= 0 ? TIER_ORDER[achievedIdx]! : 'below_floor') as TierName]),
                          n: fmt(Math.max(0, th[TIER_ORDER[targetIdx]!] - monthTotal)),
                          next: t(TIER_LABEL_KEY[TIER_ORDER[targetIdx]!]),
                          th: fmt(th[TIER_ORDER[targetIdx]!]),
                          month: periodMonthName,
                        })
                      : t('f5r8c.esc.noteTop', {
                          cur: t(TIER_LABEL_KEY[TIER_ORDER[3]]),
                          month: periodMonthName,
                        })}
                  </p>
                </>
              ) : (
                <p className="m-0 text-[13px] text-nav-slate">{t('f5r8c.esc.noLadder')}</p>
              )}
            </div>
          </div>

          {/* ==== SU VOLUMEN EN EL TIEMPO ==== */}
          <div className={card}>
            <div className="flex items-center justify-between gap-3.5 flex-wrap mb-[18px]">
              <div className="min-w-0">
                <p className="m-0 mb-1 font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-teal">
                  {t('f5r8c.vol.kicker')}
                </p>
                <p className="m-0 text-[12.5px] text-nav-slate tabular-nums">{granNote}</p>
              </div>
              <div className="flex items-center gap-[2px] bg-nav-soft border-[1.5px] border-nav-card rounded-[10px] p-[3px]">
                {(['semana', 'mes', 'anio'] as const).map((g) => {
                  const on = gran === g;
                  const needsSeries = g !== 'mes' && !hasSeries;
                  return (
                    <button
                      key={g}
                      type="button"
                      aria-pressed={on}
                      disabled={needsSeries}
                      title={needsSeries ? t('f5r8c.vol.needsServer') : undefined}
                      onClick={() => setGran(g)}
                      className={cn(
                        'min-h-[44px] px-4 rounded-lg font-head text-[12.5px] font-bold transition-colors duration-150 motion-reduce:transition-none',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2',
                        on ? 'bg-nav-navy text-white hover:bg-nav-navyHover' : 'text-nav-slate hover:bg-white hover:text-nav-navy',
                        needsSeries && 'opacity-40 cursor-not-allowed',
                      )}
                    >
                      {granDef[g].label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* El cambio de vista se anuncia junto al total nuevo (a11y). */}
            <span aria-live="polite" className="sr-only">
              {granNote} · {t('f5r8c.vol.stat.total')}: {fmt(sTotal)}
            </span>

            <div className="relative flex items-end gap-1.5 h-[170px]">
              {/* Línea de meta — en semana y mes; en la anual no. */}
              {goal != null && sMax > 0 && (
                <div
                  aria-hidden
                  className="absolute left-0 right-0 border-t-[1.5px] border-dashed pointer-events-none"
                  style={{ bottom: `${26 + Math.round(Math.min(goal / sMax, 1) * 116)}px`, borderColor: BAR_LOW }}
                />
              )}
              {buckets.map((p, i) => {
                const low = p.value < sMax * 0.35;
                const last = i === buckets.length - 1;
                const h = Math.max(3, Math.round((p.value / Math.max(sMax, 1)) * 116));
                return (
                  <span
                    key={p.key}
                    title={t('f5r8c.vol.barTitle', { tick: p.tick, v: fmt(p.value) })}
                    className="flex-1 min-w-0 h-full flex flex-col justify-end"
                  >
                    {/* Cada barra lleva su valor en TEXTO encima además del title. */}
                    <span
                      className="block mb-1.5 text-center font-head text-[10px] font-bold tabular-nums whitespace-nowrap overflow-hidden"
                      style={{ color: p.value === 0 ? BAR_LOW : '#6b7c8c' }}
                    >
                      {fmt(p.value)}
                    </span>
                    <span
                      aria-hidden
                      className="block rounded-t-[3px]"
                      style={{
                        height: `${armed ? h : 3}px`,
                        background: p.value === 0 ? BAR_ZERO : low ? BAR_LOW : last ? BAR_LAST : BAR_TEAL,
                        transition: 'height .3s cubic-bezier(.4,0,.2,1)',
                      }}
                    />
                    <span
                      className={cn('block h-[18px] mt-2 text-center font-body text-[10px] tabular-nums whitespace-nowrap overflow-hidden', last ? 'font-bold' : 'font-normal')}
                      style={{ color: last ? '#122b44' : BAR_LOW }}
                    >
                      {p.tick}
                    </span>
                  </span>
                );
              })}
            </div>

            {goal != null && (
              <p className="m-0 mt-4 font-body text-[12.5px] leading-[1.55] text-nav-slate tabular-nums">
                {t('f5r8c.vol.goalNote', { g: fmt(goal), unit: granDef[gran].unit, k: goalHit, n: buckets.length })}
              </p>
            )}

            <div className="flex gap-[26px] flex-wrap mt-[18px] pt-[18px] border-t border-nav-hair">
              {[
                { v: fmt(sTotal), l: t('f5r8c.vol.stat.total') },
                { v: fmt(Math.round(sTotal / Math.max(activeBuckets, 1))), l: t('f5r8c.vol.stat.avg', { unitActive: granDef[gran].unitActive }) },
                { v: fmt(sMax), l: t('f5r8c.vol.stat.best', { unit: granDef[gran].unit }) },
                { v: String(buckets.length - activeBuckets), l: t('f5r8c.vol.stat.inactive', { plural: granDef[gran].plural }) },
              ].map((st) => (
                <span key={st.l}>
                  <span className="block font-head text-[22px] font-extrabold leading-none tracking-[-0.02em] tabular-nums">{st.v}</span>
                  <span className="block mt-1.5 font-body text-[12.5px] text-nav-slate">{st.l}</span>
                </span>
              ))}
            </div>
          </div>

          <div className="h-4" />

          <div className="grid [grid-template-columns:repeat(auto-fit,minmax(320px,1fr))] gap-4">
            {/* ==== CONSTANCIA ==== */}
            <div className={card}>
              <p className={kicker}>{t('f5r8c.con.kicker')}</p>
              <div className="flex gap-6 flex-wrap mb-[18px]">
                <span>
                  <span className="block font-head text-[26px] font-extrabold leading-none tracking-[-0.02em] tabular-nums">
                    {daysWorking}
                  </span>
                  <span className="block mt-1.5 font-body text-[12.5px] text-nav-slate">
                    {t('f5r8c.con.streakLabel', { m: fmt(target) })}
                  </span>
                </span>
                <span>
                  <span className="block font-head text-[26px] font-extrabold leading-none tracking-[-0.02em] tabular-nums">
                    {activeDays != null ? activeDays : '—'}
                  </span>
                  <span className="block mt-1.5 font-body text-[12.5px] text-nav-slate">
                    {t('f5r8c.con.activeLabel', { month: periodMonthName })}
                  </span>
                </span>
              </div>
              <div className="h-2 rounded bg-nav-hair overflow-hidden">
                <div
                  className="h-2 rounded bg-nav-sage"
                  style={{
                    width: `${armed ? Math.min(100, (daysWorking / STREAK_DIAMANTE_DE_CONSTANCIA_MIN) * 100) : 0}%`,
                    transition: 'width .9s cubic-bezier(.34,1.56,.64,1)',
                  }}
                />
              </div>
              <p className="m-0 mt-3 text-[12.5px] leading-[1.6] text-nav-slate">
                {cutDay != null && (
                  <>
                    {t('f5r8c.con.cut', {
                      date: new Intl.DateTimeFormat(lang === 'es' ? 'es' : 'en', { day: 'numeric', month: 'long' }).format(new Date(cutDay + 'T12:00:00Z')),
                    })}{' '}
                  </>
                )}
                {s.achievements.diamanteDeConstancia
                  ? t('f5r8c.con.noteEarned', { min: STREAK_DIAMANTE_DE_CONSTANCIA_MIN })
                  : t('f5r8c.con.noteLocked', { min: STREAK_DIAMANTE_DE_CONSTANCIA_MIN, d: daysWorking })}{' '}
                {t('f5r8c.con.rule')}
              </p>
            </div>

            {/* ==== LOGROS ==== */}
            <div className={card}>
              <div className="flex items-baseline justify-between gap-3 mb-3.5">
                <p className="m-0 font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-teal">
                  {t('f5r8c.log.kicker')}
                </p>
                <span className="text-[12.5px] text-nav-slate tabular-nums">
                  {t('f5r8c.log.count', { k: achEarned, m: achievements.length })}
                </span>
              </div>

              {virgin ? (
                // Sin screenings: sin logros bloqueados listados (estado propio).
                <p className="m-0 text-[13px] text-nav-slate">{t('f5r8c.log.none')}</p>
              ) : (
                <div className="flex flex-col gap-2">
                  {achievements.map((a) => (
                    <div
                      key={a.key}
                      className="flex items-center gap-3 min-h-[52px] px-[13px] py-[9px] rounded-[10px] border-[1.5px]"
                      style={
                        a.earned
                          ? { background: '#f4fafa', borderColor: '#c9e2e2' }
                          : { background: '#fafbfc', borderColor: '#e3e8eb' }
                      }
                    >
                      <span
                        className={cn(
                          'flex-none inline-flex items-center justify-center w-7 h-7 rounded-full',
                          a.earned ? 'bg-nav-teal text-white' : 'bg-nav-hair text-nav-slate',
                        )}
                      >
                        {a.earned ? (
                          <Check className="w-[15px] h-[15px]" strokeWidth={2.6} aria-hidden />
                        ) : (
                          <Lock className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
                        )}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block font-head text-[13px] font-bold text-nav-navy">{a.name}</span>
                        {/* Un logro bloqueado dice su condición en palabras (a11y + dibujo). */}
                        <span className="block mt-[3px] font-body text-[11.5px] leading-[1.45] text-nav-slate tabular-nums">
                          {a.note}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* «Ver sus screenings» → la Ficha de agente (R6a). */}
          <AgentDetailModal agentId={showScreenings ? String(agentId) : null} onClose={() => setShowScreenings(false)} />
        </>
      )}
    </div>
  );
}
